import React, { useEffect, useState } from 'react';
import { BarChart3, ChevronDown, ListOrdered, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from 'src/components/ui/dropdown-menu';
import { Button } from 'src/components/ui/button';

const AVAILABLE_METRICS = ['Impressions', 'Clicks', 'Spend', 'Conversions'];

export interface MetricRankDisplayData {
  [key: string]: string | number;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  initialMetric: string;
}


interface MetricRankDisplayProps {
  title: string;
  labelKey: string;
  data: MetricRankDisplayData[];
  onMetricChange?: (metric: string) => void;
  className?: string;
  isLoading?: boolean;
  initialMetric?: string;
}

const MetricRankDisplay: React.FC<MetricRankDisplayProps> = ({
  title,
  labelKey,
  data,
  onMetricChange,
  className = '',
  isLoading = false,
  initialMetric = AVAILABLE_METRICS[0],
}) => {
  const [selectedMetric, setSelectedMetric] = useState(initialMetric);
  const [sortedData, setSortedData] = useState<MetricRankDisplayData[]>([]);
  // Sort data by selected metric in descending order

  useEffect(() => {
    const sortedData = [...data].sort((a, b) => {
      const aValue = a[selectedMetric.toLowerCase() as keyof MetricRankDisplayData] as number;
      const bValue = b[selectedMetric.toLowerCase() as keyof MetricRankDisplayData] as number;
      return bValue - aValue;
    }).slice(0, 11); // Get top 5
    setSortedData(sortedData);
  }, [data, selectedMetric]);

  const formatNumber = (amount: number): string => {
    if (amount >= 100000) { // 6+ digits
      if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(1).replace('.0', '')}M`;
      }
      return `${(amount / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Loading
            {' '}
            {title}
            {' '}
            Rankings
          </h3>
          <p className="text-sm text-gray-600 text-center">
            Processing
            {' '}
            {title}
            {' '}
            data and fetching rankings...
          </p>
        </div>
      </div>
    );
  }

  if (!sortedData.length) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-8 w-3/4 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <ListOrdered className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Ranking Data
          </h3>
          <p className="text-sm text-gray-600 text-center">
            No
            {' '}
            {title}
            {' '}
            metrics data available to display for rankings.
          </p>
        </div>
      </div>
    );
  }

  if (!sortedData.length) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-8 w-3/4 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <BarChart3 className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Ranking Data Available
          </h3>
          <p className="text-sm text-gray-600 text-center">
            Could not determine rankings for the provided
            {' '}
            {title}
            ..
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      <div className="flex justify-between bg-gray-201">
        <div className="flex items-center justify-between w-full">
          <div className="flex gap-2 p-2 pl-4 items-center font-medium">
            <ListOrdered className="w-4 h-4" />
            {title}
          </div>
          {/* <div className="border-l border-gray-401 h-1/2 ml-4 mr-2" /> */}
          <div className="flex items-center pr-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="med"
                >
                  <ChevronDown className="w-4 h-4" />
                  {AVAILABLE_METRICS.find((metric) => metric.toLowerCase() === selectedMetric.toLowerCase())}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='z-[999]'>
                {AVAILABLE_METRICS.filter((metric) => metric.toLowerCase() !== selectedMetric.toLowerCase()).map((metric) => (
                  <DropdownMenuItem
                    key={metric}
                    onClick={() => setSelectedMetric(metric)}
                  >
                    {metric}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>


      <div className="flex justify-between px-4 border-b border-gray-200 py-1" >
        <div className="text-sm font-light">
          {labelKey}
        </div>
        <div className="text-sm font-light">
          {AVAILABLE_METRICS.find((metric) => metric.toLowerCase() === selectedMetric.toLowerCase())}
        </div>
      </div>

      <div className="p-4">
        {sortedData.length > 0 ? (
          <div className="space-y-3">
            {sortedData.map((item, index) => (
              <div
                key={`${item[labelKey.toLocaleLowerCase() as keyof MetricRankDisplayData]}-${index}`}
                className="flex justify-between items-center"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {item[labelKey.toLocaleLowerCase() as keyof MetricRankDisplayData]}
                  </span>
                </div>
                <span className="text-sm">
                  {selectedMetric === 'ctr' || selectedMetric === 'cpa' || selectedMetric === 'cpc'
                    ? `${(item[selectedMetric.toLowerCase() as keyof MetricRankDisplayData] as number || 0).toFixed(2)}%`
                    : formatNumber(item[selectedMetric.toLowerCase() as keyof MetricRankDisplayData] as number || 0)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">
            No data available
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricRankDisplay;
